/* eslint-disable @typescript-eslint/no-shadow */
import { createTheme } from '@mui/material';

import { colors, error, primary, secondary, success } from './colors';
import InterBold from './fonts/Inter-Bold.ttf'; // 700
import Inter from './fonts/Inter-Medium.ttf'; // 500
import InterRegular from './fonts/Inter-Regular.ttf'; // 400
import InterSemiBold from './fonts/Inter-SemiBold.ttf'; // 600
import { MuiAppBar } from './MuiAppBar';
import { MuiButton } from './MuiButton';
import { MuiTextField } from './MuiTextField';
import { typography } from './typography';
import { boldMonochromeComponents } from '@/styles/BoldMonochromeTheme';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

export const important = (cssValue: string | number) => `${cssValue} !important`;

export const theme = createTheme({
  colors,
  palette: {
    mode: 'dark',
    // Cyan primary
    primary: {
      main: primary.main,
      dark: primary.dark,
      light: primary.light,
      contrastText: colors.black,
    },
    // Dark secondary
    secondary: {
      main: secondary.main,
      dark: secondary.dark,
      light: secondary.light,
      contrastText: colors.white,
    },
    success: {
      main: success.main,
      dark: success.dark,
      light: success.light,
    },
    error: {
      main: error.main,
      dark: error.dark,
      light: error.light,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
      disabled: colors.disabled,
    },
  },
  typography,
  breakpoints: {
    values: {
      xs: 576,
      sm: 768,
      md: 1024,
      lg: 1280,
      xl: 2160,
      xxl: 2880,
    },
  },
  components: {
    ...boldMonochromeComponents,
    MuiAppBar,
    MuiButton,
    MuiTextField,
    MuiCssBaseline: {
      styleOverrides: theme => `
        @font-face {
          font-family: 'InterRegular';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Inter'), local('InterRegular'), url(${InterRegular}) format('opentype');
        }

        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: local('Inter'), local('Inter'), url(${Inter}) format('opentype');
        }
        
        @font-face {
          font-family: 'InterSemiBold';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: local('Inter'), local('InterSemiBold'), url(${InterSemiBold}) format('opentype');
        }

        @font-face {
          font-family: 'InterBold';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: local('Inter'), local('InterBold'), url(${InterBold}) format('opentype');
        }

        body {
          font-family: 'Inter', 'Helvetica', 'Arial', 'sans-serif';
          color: ${theme.colors.text}
        }
      `,
    },
  },
});

theme.shadows.unshift('0px 26px 58px 0px rgba(0,0,0,0.22), 0px 5px 14px 0px rgba(0,0,0,0.18);');

// Light mode colors - complete override of all nested color objects
const lightColors = {
  ...colors,
  white: '#171717',
  black: '#fafafa',
  text: '#171717',
  textSecondary: '#525252',
  textTertiary: '#737373',
  disabled: 'rgba(0, 0, 0, 0.3)',
  background: {
    default: '#fafafa',
    paperElevation0: '#f5f5f5',
    elevated: '#ffffff',
    disabledButton: '#e5e5e5',
  },
  // Light mode secondary colors
  secondary: {
    main: '#525252',
    dark: '#171717',
    light: '#737373',
    background: '#f1f5f9',  // Light gray for card backgrounds
    border: '#e2e8f0',
    button: '#94a3b8',
    outline: '#cbd5e1',
  },
  // Light mode action colors
  action: {
    active: 'rgba(0, 0, 0, 0.56)',
    hover: 'rgba(0, 163, 184, 0.08)',
    selected: 'rgba(0, 163, 184, 0.16)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    focus: 'rgba(0, 163, 184, 0.12)',
    disabled: 'rgba(0, 0, 0, 0.3)',
    divider: '#e2e8f0',
  },
  // Light mode info colors
  info: {
    main: '#0891b2',
    dark: '#0e7490',
    light: '#22d3ee',
  },
  // Light mode primary colors
  primary: {
    main: '#00a3b8',
    dark: '#007d8f',
    light: '#00c8e0',
    hover: 'rgba(0, 163, 184, 0.08)',
    selected: 'rgba(0, 163, 184, 0.16)',
    focus: 'rgba(0, 163, 184, 0.12)',
    focusVisible: 'rgba(0, 163, 184, 0.4)',
    outlineBorder: 'rgba(0, 163, 184, 0.5)',
    list: '#f1f5f9',
  },
};

// Light theme for when user toggles to light mode
export const lightTheme = createTheme({
  colors: lightColors,
  palette: {
    mode: 'light',
    primary: {
      main: '#00a3b8',
      dark: '#007d8f',
      light: '#00c8e0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#525252',
      dark: '#171717',
      light: '#737373',
      contrastText: '#ffffff',
    },
    success: {
      main: '#16a34a',
      dark: '#15803d',
      light: '#22c55e',
    },
    error: {
      main: '#dc2626',
      dark: '#b91c1c',
      light: '#ef4444',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#171717',
      secondary: '#525252',
      disabled: '#a3a3a3',
    },
  },
  typography,
  breakpoints: {
    values: {
      xs: 576,
      sm: 768,
      md: 1024,
      lg: 1280,
      xl: 2160,
      xxl: 2880,
    },
  },
  components: {
    ...boldMonochromeComponents,
    MuiAppBar,
    MuiButton,
    MuiTextField,
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'InterRegular';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Inter'), local('InterRegular'), url(${InterRegular}) format('opentype');
        }

        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: local('Inter'), local('Inter'), url(${Inter}) format('opentype');
        }

        @font-face {
          font-family: 'InterSemiBold';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: local('Inter'), local('InterSemiBold'), url(${InterSemiBold}) format('opentype');
        }

        @font-face {
          font-family: 'InterBold';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: local('Inter'), local('InterBold'), url(${InterBold}) format('opentype');
        }

        body {
          font-family: 'Inter', 'Helvetica', 'Arial', 'sans-serif';
          color: #171717;
          background-color: #fafafa;
        }
      `,
    },
  },
});

lightTheme.shadows.unshift('0px 26px 58px 0px rgba(0,0,0,0.1), 0px 5px 14px 0px rgba(0,0,0,0.08);');
